import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs";
import { SpotTruckInspectionSettingsModel } from "src/app/api/api-types";
import { ApiUrl } from "src/app/api/api-url-helper";
import { RequestService } from "src/app/core/request.service";
import { SitesService } from "src/app/core/sites";
import { SpotTruckInspectionSettings } from "src/app/partner/site-settings/spot-truck-inspection/spot-truck-inspection.model";
import { pluckDistinctUntilChanged } from "src/utils";
import { SiteSettingsService } from "./site-settings.service";

@Injectable({ providedIn: "root" })
export class SpotTruckInspectionSettingsService {
  public constructor(
    private readonly request: RequestService,
    private readonly siteSettings: SiteSettingsService,
    private readonly sites: SitesService,
  ) {}

  private readonly updates =
    new BehaviorSubject<SpotTruckInspectionSettings | null>(null);

  private readonly updateChanges = this.sites.selectedChanges.pipe(
    pluckDistinctUntilChanged("id"),
    tap(() => {
      // resting the updates subject whenever the site changed.
      if (this.updates.value) {
        this.updates.next(null);
      }
    }),
    shareReplay(1),
  );

  public readonly settingsChanges = merge(
    this.updates,
    this.siteSettings.changes.pipe(
      pluckDistinctUntilChanged("spotTruckInspectionSettings"),
      distinctUntilChanged(),
    ),
  ).pipe(
    withLatestFrom(this.updateChanges),
    map(([settings]) => settings),
    shareReplay(1),
  );

  public readonly isAdHocSettingEnabledChanges = this.settingsChanges.pipe(
    pluckDistinctUntilChanged("isAdHoc"),
  );

  private readonly isExpiredChanges = combineLatest([
    this.sites.selectedChanges,
    this.settingsChanges.pipe(pluckDistinctUntilChanged("isExpired")),
  ]).pipe(
    switchMap(([site, isExpiredSettingEnabled]) =>
      isExpiredSettingEnabled
        ? this.request.get({
            prefix: site.getApiUrl(),
            path: ApiUrl.SPOT_TRUCK_INSPECTION_FORM_REQUIRED,
          })
        : of(isExpiredSettingEnabled),
    ),
  );

  public readonly shouldCompleteAfterLoginChanges = combineLatest([
    this.settingsChanges.pipe(pluckDistinctUntilChanged("isAtLogIn")),
    this.isExpiredChanges,
  ]).pipe(
    map(
      ([isAtLoginSettingEnabled, isExpired]) =>
        isAtLoginSettingEnabled || isExpired,
    ),
  );

  public update(
    body: SpotTruckInspectionSettings,
  ): Observable<SpotTruckInspectionSettingsModel> {
    return this.sites.selectedChanges.pipe(
      switchMap((site) =>
        this.request.post({
          prefix: site.getApiUrl(),
          path: ApiUrl.MANAGE_SPOT_TRUCK_INSPECTION_SETTINGS,
          body: SpotTruckInspectionSettings.serialize(body),
        }),
      ),
      tap((updatedSettings) => {
        this.updates.next(
          SpotTruckInspectionSettings.deserialize(updatedSettings),
        );
      }),
    );
  }
}
